import { site, whatsappEnquiry } from '../data/site';
import { Icon, Ornament, Reveal, SectionHeading } from '../components/Primitives';
import Seo from '../components/Seo';
import { PageHero } from '../components/Shared';

/* Keyless embed — resolves from the address, so no API key to leak or expire. */
const MAP_EMBED = `https://www.google.com/maps?q=${encodeURIComponent(
  `${site.name}, ${site.addressOneLine}`
)}&output=embed`;

const CONTACTS = [
  {
    Glyph: Icon.Pin,
    label: 'Showroom',
    lines: [site.address.line1, `${site.address.line2}, ${site.address.city}`, `${site.address.state} ${site.address.postalCode}`],
    action: { text: 'Open in Maps', href: site.social.map, external: true },
  },
  {
    Glyph: Icon.Phone,
    label: 'Telephone',
    lines: [site.phone],
    action: { text: 'Call now', href: site.phoneHref },
  },
  {
    Glyph: Icon.Mail,
    label: 'Email',
    lines: [site.email],
    action: { text: 'Write to us', href: site.emailHref },
  },
  {
    Glyph: Icon.Clock,
    label: 'Opening hours',
    lines: site.hours.map((h) => `${h.days} · ${h.time}`),
    action: { text: 'Book a viewing', href: whatsappEnquiry('Booking a showroom viewing'), external: true },
  },
];

export default function Visit() {
  return (
    <>
      <Seo
        title="Visit Us"
        description={`Nikhil Jewellers — ${site.addressOneLine}. Call ${site.phone} or message on WhatsApp to book a bridal viewing.`}
        image="/assets/showroom/showroom-signage.webp"
      />

      <PageHero
        eyebrow="Visit Us"
        title="Ten Eleven Building, C G Road"
        lede="Walk in during showroom hours, or message ahead and we will have a selection waiting. Bridal viewings are best booked, so we can give you the time."
        image="/assets/showroom/showroom-signage.webp"
        trail={[{ label: 'Home', to: '/' }, { label: 'Visit Us' }]}
      />

      {/* Contact cards */}
      <section className="bg-cream-50 py-16 sm:py-20">
        <div className="shell">
          <div className="grid gap-px bg-royal-800/10 sm:grid-cols-2 lg:grid-cols-4">
            {CONTACTS.map(({ Glyph, label, lines, action }, i) => (
              <Reveal
                key={label}
                delay={i * 80}
                className="flex flex-col bg-cream-50 p-7"
              >
                <Glyph className="h-6 w-6 text-gold-600" />
                <h2 className="mt-5 text-[11px] font-medium uppercase tracking-wide2 text-royal-800/60">
                  {label}
                </h2>
                <div className="mt-2.5 flex-1 space-y-0.5">
                  {lines.map((l) => (
                    <p key={l} className="text-[14.5px] leading-relaxed text-royal-900">
                      {l}
                    </p>
                  ))}
                </div>
                <a
                  href={action.href}
                  {...(action.external
                    ? { target: '_blank', rel: 'noreferrer noopener' }
                    : {})}
                  className="link-underline mt-5 inline-flex items-center gap-2 text-[11px]
                    uppercase tracking-wide2 text-maroon-700"
                >
                  {action.text}
                  <Icon.Arrow className="h-3.5 w-3.5" />
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Map + directions */}
      <section className="bg-cream-100 py-16 sm:py-24">
        <div className="shell">
          <div className="grid gap-10 lg:grid-cols-12 lg:gap-14">
            <Reveal className="lg:col-span-5">
              <p className="eyebrow">Finding Us</p>
              <h2 className="display-lg mt-4 text-royal-900">On C G Road</h2>
              <Ornament className="mt-6 !justify-start" />
              <p className="mt-7 lede">
                We are on the third floor of the Ten Eleven Building, one of the
                landmark commercial blocks on C G Road in Navrangpura — central,
                and easy to reach from anywhere in Ahmedabad.
              </p>

              <dl className="mt-9 space-y-5 border-t border-royal-800/10 pt-7">
                {[
                  ['Landmark', 'Ten Eleven Building, C G Road, Navrangpura'],
                  ['Floor', 'Unit 305, third floor'],
                  ['Parking', 'Street and basement parking along C G Road'],
                  ['Nearest hub', 'Swastik Cross Roads / Navrangpura'],
                ].map(([k, v]) => (
                  <div key={k} className="grid grid-cols-3 gap-4">
                    <dt className="text-[11px] uppercase tracking-wide2 text-royal-800/55">
                      {k}
                    </dt>
                    <dd className="col-span-2 text-[14.5px] leading-relaxed text-royal-900">
                      {v}
                    </dd>
                  </div>
                ))}
              </dl>

              <div className="mt-9 flex flex-wrap gap-3">
                <a
                  href={site.social.map}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="btn-primary"
                >
                  <Icon.Pin className="h-4 w-4" />
                  Get directions
                </a>
                <a
                  href={whatsappEnquiry('Booking a showroom viewing')}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="btn-outline"
                >
                  <Icon.WhatsApp className="h-4 w-4" />
                  Book a viewing
                </a>
              </div>
            </Reveal>

            <Reveal delay={120} className="lg:col-span-7">
              {/*
                The keyless maps embed is script-driven and can be blocked by strict
                privacy settings or an ad blocker. A designed panel sits underneath
                so the area reads as intentional rather than as a broken frame; the
                iframe simply covers it when it loads.
              */}
              <div className="relative h-full min-h-[24rem] overflow-hidden border border-royal-800/10">
                <div className="noise-navy absolute inset-0 flex flex-col items-center justify-center gap-4 p-8 text-center">
                  <img
                    src="/assets/logo/monogram-white.png"
                    alt=""
                    aria-hidden="true"
                    className="h-16 w-auto opacity-40"
                  />
                  <p className="font-display text-2xl font-light text-cream-50">
                    {site.address.line1}
                  </p>
                  <p className="text-[13.5px] leading-relaxed text-cream-200/70">
                    {site.address.line2}, {site.address.city}
                    <br />
                    {site.address.state} {site.address.postalCode}
                  </p>
                  <a
                    href={site.social.map}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="btn-outline-light mt-2"
                  >
                    <Icon.Pin className="h-4 w-4" />
                    Open in Google Maps
                  </a>
                </div>

                <iframe
                  title={`Map showing ${site.name} on C G Road, Ahmedabad`}
                  src={MAP_EMBED}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="relative z-10 h-full min-h-[24rem] w-full border-0"
                  allowFullScreen
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* What to expect */}
      <section className="noise-navy relative overflow-hidden py-20 sm:py-24">
        <div className="shell relative">
          <SectionHeading
            tone="light"
            eyebrow="Your Visit"
            title="What to expect"
            lede="A bridal appointment usually runs an hour or more. Bring the outfit, or a photograph of it — matching the jewellery to the fabric matters more than most people expect."
          />

          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {[
              [
                'Bring the outfit',
                'Colour and neckline decide the length of the collar and how the haar falls. A photo on your phone is enough.',
              ],
              [
                'Try the full suite',
                'Pieces are shown together — collar, haar, chandbali, tikka, kada — because that is how they will be worn.',
              ],
              [
                'Adjustments in-house',
                'Sizing, lengths and clasps are altered on the premises, so nothing goes home not quite fitting.',
              ],
            ].map(([title, text], i) => (
              <Reveal key={title} delay={i * 100}>
                <div className="border-t border-gold-500/30 pt-6">
                  <p className="font-display text-5xl font-light text-gold-400/45">0{i + 1}</p>
                  <h3 className="mt-4 font-display text-2xl font-normal text-cream-50">{title}</h3>
                  <p className="mt-3 text-[14px] leading-relaxed text-cream-200/70">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-16 text-center">
            <div className="flex flex-wrap items-center justify-center gap-3">
              <a
                href={whatsappEnquiry('Booking a bridal appointment')}
                target="_blank"
                rel="noreferrer noopener"
                className="btn-gold"
              >
                <Icon.WhatsApp className="h-4 w-4" />
                Book a bridal appointment
              </a>
              <a href={site.phoneHref} className="btn-outline-light">
                <Icon.Phone className="h-4 w-4" />
                {site.phone}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Follow */}
      <section className="bg-cream-50 py-16 sm:py-20">
        <div className="shell">
          <SectionHeading
            eyebrow="Follow Along"
            title="New arrivals go up first on Instagram"
          />
          <Reveal className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <a
              href={site.social.instagram}
              target="_blank"
              rel="noreferrer noopener"
              className="btn-primary"
            >
              <Icon.Instagram className="h-4 w-4" />
              @nikhiljewellersahmedabad
            </a>
            <a
              href={site.social.facebook}
              target="_blank"
              rel="noreferrer noopener"
              className="btn-outline"
            >
              <Icon.Facebook className="h-4 w-4" />
              Facebook
            </a>
          </Reveal>
        </div>
      </section>
    </>
  );
}
